import { Dispatch } from "redux";
import { batch } from "react-redux";

import { DeleteColors, GetColors } from "../../../connection/colors";
import {
	AddNewSession,
	DeleteSession,
	DuplicateSession,
	LoadEvent,
	SaveEvent,
	UpdateSessionInfo,
	PublishEvent,
	UpdateRegistrationSettings,
	AddPostRegisterHomePageModuleToEvent,
	AddHomepageToEvent,
	CheckEventPublishedStatus,
	UpdateEventFavicon,
	DeleteEventFavicon,
} from "../../../connection/create-event";
import { DeleteFonts, GetFonts } from "../../../connection/fonts";
import { GetTemplates } from "../../../connection/templates";
import { Action, AsyncAction } from "../../../types/actions";
import {
	BrandliveEvent,
	Color,
	CreateSession,
	DesignShape,
	DesignStyle,
	FontPack,
	HomepageFooter,
	HomepageHeader,
	IHomepageMainNavItems,
	IHomepageProfileNavItems,
	PageModule,
	RegistrationQuestion,
	Requirement,
	Session,
	Template,
	SingleSignOnTypes,
	GateTypes,
	RegistrationStep,
	RegistrationStepType,
	Registration,
	SessionTabsLayout,
	IPostRegisterHomeModule,
	TicketingSet,
	TranslateString,
	BLAdmin,
	EventHomepage
} from "../../../types/working-model";
import { GetEmptyEvent } from "../../utils/create-event";
import { getEvents, UPDATE_EVENTS_NAME } from "./events";
import { ThunkDispatch } from "redux-thunk";

export const CREATE_NEW_EVENT = "CREATE_NEW_EVENT";
export function createNewEvent(channel: number, template: Template): Action {
	return {
		type: CREATE_NEW_EVENT,
		payload: GetEmptyEvent(channel, template)
	};
}

export const SET_EVENT_NAME = "SET_EVENT_NAME";
export function setEventName(name: string): Action {
	return {
		type: SET_EVENT_NAME,
		payload: name
	};
}

export const LOAD_TEMPLATES = "LOAD_TEMPLATES";
export function loadTemplates(channel: number, token: string): AsyncAction {
	return {
		type: LOAD_TEMPLATES,
		promise: GetTemplates(channel, token)
	};
}

interface ICreateNewEventSession {
	session: CreateSession;
	workingEvent: BrandliveEvent;
	active_channel: number;
	token: string;
	mulitpleSessions?: 'multi';
	user: BLAdmin;
}

// we're using redux thunk here to be able to wait for multiple calls before dispatching,
// otherwise (with redux pack and the way we're handling updates) we end up with time races between dispatches and things can be overwritten
export const ADD_HOMEPAGE_TO_WORKING_EVENT = "ADD_HOMEPAGE_TO_WORKING_EVENT";
export const ADD_NEW_SESSION_TO_STORE = "ADD_NEW_SESSION_TO_STORE";
export const CREATE_NEW_EVENT_SESSION = "CREATE_NEW_EVENT_SESSION";
export const createNewEventSession = (data: ICreateNewEventSession, cb?: () => void) => async (dispatch: ThunkDispatch<any, void, Action | AsyncAction>): Promise<any> => {
	try {
		const {
			session,
			workingEvent,
			active_channel,
			token,
			mulitpleSessions,
			user
		} = data;
		if (workingEvent?.event) {
			const newSession = await AddNewSession(session, workingEvent.event, workingEvent.uuid, active_channel, token);
			let homepage: EventHomepage;
			if (!workingEvent?.homepage) {
				homepage = await AddHomepageToEvent(workingEvent.uuid, token, mulitpleSessions);
			}
			// dispatch all calls to update redux in various places
			batch(() => {
				if (homepage) {
					dispatch({
						type: ADD_HOMEPAGE_TO_WORKING_EVENT,
						payload: homepage,
					});
				}
				dispatch({
					type: ADD_NEW_SESSION_TO_STORE,
					payload: newSession,
				});
				dispatch(getEvents(user.active_channel, token));
			});
		}
	} catch (e) {
		console.error(e);
	} finally {
		if (cb) cb();
	}
};

export const CREATE_NEW_SESSION = "CREATE_NEW_SESSION";
export function createNewSession(newSession: CreateSession): Action {
	return {
		type: CREATE_NEW_SESSION,
		payload: newSession
	};
}

export const ADD_NEW_SESSION = "ADD_NEW_SESSION";
export function addNewSession(
	newSession: CreateSession,
	event: number,
	eventUuid: string,
	channel: number,
	token: string,
	cb?: () => void,
): AsyncAction {
	return {
		type: ADD_NEW_SESSION,
		promise: AddNewSession(newSession, event, eventUuid, channel, token, cb)
	};
}

export const DUPLICATE_SESSION = "DUPLICATE_SESSION";
export function duplicateSession(
	session: Session,
	event: number,
	eventUuid: string,
	token: string
): AsyncAction {
	return {
		type: DUPLICATE_SESSION,
		promise: DuplicateSession(session, event, eventUuid, token)
	};
}

export const DELETE_SESSION = "DELETE_SESSION";
export function deleteSession(
	sessionID: number,
	eventUuid: string,
	token: string
): AsyncAction {
	return {
		type: DELETE_SESSION,
		promise: DeleteSession(sessionID, eventUuid, token)
	};
}

export const REMOVE_SESSION = "REMOVE_SESSION";
export function removeSessionFromCreateEventReducer(sessionID: number, eventUuid: string): Action {
	return {
		type: REMOVE_SESSION,
		payload: { session: sessionID, eventUuid }
	};
}

export const REMOVE_EVENT = "REMOVE_EVENT";
export function removeEventFromCreateEventReducer(eventUuid: string): Action {
	return {
		type: REMOVE_EVENT,
		payload: eventUuid
	};
}

export const UPDATE_SESSION_INFO = "UPDATE_SESSION_INFO";
export function updateSessionInfo(
	session: Session,
	token: string
): AsyncAction {
	return {
		type: UPDATE_SESSION_INFO,
		promise: UpdateSessionInfo(session, token)
	};
}

export const SET_WORKING_EVENT = "SET_WORKING_EVENT";
export function setWorkingEvent(newEvent: BrandliveEvent): Action {
	return {
		type: SET_WORKING_EVENT,
		payload: newEvent
	};
}

export const SET_SELECTED_EVENT = "SET_SELECTED_EVENT";
export function setSelectedEvent(event: BrandliveEvent): Action {
	return {
		type: SET_SELECTED_EVENT,
		payload: event
	};
}

export const CLEAR_SELECTED_EVENT = "CLEAR_SELECTED_EVENT";
export function clearSelectedEvent(): Action {
	return {
		type: CLEAR_SELECTED_EVENT,
		payload: null
	};
}

export const CLEAR_WORKING_EVENT = "CLEAR_WORKING_EVENT";
export function clearWorkingEvent(): Action {
	return {
		type: CLEAR_WORKING_EVENT,
		payload: null
	};
}

export const CLEAR_PUBLISHED_URL = "CLEAR_PUBLISHED_URL";
export function clearPublishedUrl(): Action {
	return {
		type: CLEAR_PUBLISHED_URL,
		payload: null,
	};
}

export const BLUR_EDITOR = "BLUR_EDITOR";
export function blurEditor(shouldBlur: boolean): Action {
	return {
		type: BLUR_EDITOR,
		payload: { shouldBlur },
	};
}

export const LOAD_WORKING_EVENT = "LOAD_WORKING_EVENT";
export function loadWorkingEvent(uuid: string, token: string): AsyncAction {
	return {
		type: LOAD_WORKING_EVENT,
		promise: LoadEvent(uuid, token),
	};
}

export const SET_REGISTRATION_ON = "SET_REGISTRATION_ON";
export function setRegistrationOn(on: boolean): Action {
	return {
		type: SET_REGISTRATION_ON,
		payload: on
	};
}

export const SET_REGISTRATION_TYPE = "SET_REGISTRATION_TYPE";
export function setRegistrationType(type: number): Action {
	return {
		type: SET_REGISTRATION_TYPE,
		payload: type
	};
}

export const UPDATE_REGISTRATION_SETTINGS = "UPDATE_REGISTRATION_SETTINGS";
export function updateRegistrationSettings(token: string, event_uuid: string, settings: Registration): AsyncAction {
	return {
		type: UPDATE_REGISTRATION_SETTINGS,
		promise: UpdateRegistrationSettings(token, event_uuid, settings)
	};
}

export const ADD_REGISTRATION_REQUIREMENT = "ADD_REGISTRATION_REQUIREMENT";
export function addRegistrationRequirement(type: GateTypes, newRequirement: Requirement): Action {
	return {
		type: ADD_REGISTRATION_REQUIREMENT,
		payload: {
			type,
			newRequirement
		}
	};
}

export const REMOVE_REGISTRATION_REQUIREMENT = "REMOVE_REGISTRATION_REQUIREMENT";
export function removeRegistrationRequirement(type: GateTypes, passcodeListIndex?: number): Action {
	return {
		type: REMOVE_REGISTRATION_REQUIREMENT,
		payload: {
			type,
			passcodeListIndex
		}
	};
}

export const UPDATE_REGISTRATION_REQUIREMENT =
	"UPDATE_REGISTRATION_REQUIREMENT";
export function updateRegistrationRequirement(
	type: GateTypes,
	updatedRequirement: Requirement,
	passcodeListIndex?: number,
): Action {
	return {
		type: UPDATE_REGISTRATION_REQUIREMENT,
		payload: { type, passcodeListIndex, updatedRequirement }
	};
}

export const SET_REGISTRATION_FEATURE = "SET_REGISTRATION_FEATURE";
export function setRegistrationFeature(feature: string, on: boolean): Action {
	return {
		type: SET_REGISTRATION_FEATURE,
		payload: { feature, on }
	};
}

export const SET_REGISTRATION_SINGLE_SIGN_ON = "SET_REGISTRATION_SINGLE_SIGN_ON";
export function setRegistrationSingleSignOn(singleSignOns: SingleSignOnTypes[]): Action {
	return {
		type: SET_REGISTRATION_SINGLE_SIGN_ON,
		payload: singleSignOns
	};
}

export const ADD_REGISTRATION_QUESTION = "ADD_REGISTRATION_QUESTION";
export function addRegistrationQuestion(
	question: RegistrationQuestion
): Action {
	return {
		type: ADD_REGISTRATION_QUESTION,
		payload: question
	};
}

export const REMOVE_REGISTRATION_QUESTION = "REMOVE_REGISTRATION_QUESTION";
export function removeRegistrationQuestion(
	question: RegistrationQuestion
): Action {
	return {
		type: REMOVE_REGISTRATION_QUESTION,
		payload: question
	};
}

export const TOGGLE_REGISTRATION_QUESTION_REQUIRED =
	"TOGGLE_REGISTRATION_QUESTION_REQUIRED";
export function toggleRegistrationQuestionRequired(
	question: number,
	required: boolean
): Action {
	return {
		type: TOGGLE_REGISTRATION_QUESTION_REQUIRED,
		payload: { question, required }
	};
}

export const SET_REGISTRATION_QUESTIONS = "SET_REGISTRATION_QUESTIONS";
export function setRegistrationQuestions(
	questions: RegistrationQuestion[],
	required_questions: number[]
): Action {
	return {
		type: SET_REGISTRATION_QUESTIONS,
		payload: { questions, required_questions }
	};
}

export const CHOOSE_REGISTRATION_STEP = "CHOOSE_REGISTRATION_STEP";
export function chooseRegistrationStep(step: RegistrationStepType): Action {
	return {
		type: CHOOSE_REGISTRATION_STEP,
		payload: step
	};
}

export const TOGGLE_CUSTOMIZE_FORM_MODAL = "OPEN_CUSTOMIZE_FORM_MODAL";
export function toggleCustomizeFormModal(open: boolean): Action {
	return {
		type: TOGGLE_CUSTOMIZE_FORM_MODAL,
		payload: open
	};
}

export const UPDATE_REGISTRATION_STEPS = "UPDATE_REGISTRATION_STEPS";
export function updateRegistrationSteps(steps: RegistrationStep[]): Action {
	return {
		type: UPDATE_REGISTRATION_STEPS,
		payload: steps
	};
}

export const UPDATE_REQUIRED_REGISTRATION_QUESTIONS = "UPDATE_REQUIRED_REGISTRATION_QUESTIONS";
export function updateRequiredQuestions(requiredQuestions: number[]): Action {
	return {
		type: UPDATE_REQUIRED_REGISTRATION_QUESTIONS,
		payload: requiredQuestions
	};
}

export const LOAD_FONT_PACKS = "LOAD_FONT_PACKS";
export function loadFontPacks(channel: number, token: string): AsyncAction {
	return {
		type: LOAD_FONT_PACKS,
		promise: GetFonts(channel, token)
	};
}

export const SET_SELECTED_FONT = "SET_SELECTED_FONT";
export function setSelectedFont(font: FontPack): Action {
	return {
		type: SET_SELECTED_FONT,
		payload: font
	};
}

export const ADD_NEW_FONT_PACK = "ADD_NEW_FONT_PACK";
export function addNewFontPack(font: FontPack): Action {
	return {
		type: ADD_NEW_FONT_PACK,
		payload: font
	};
}

export const DELETE_FONT_PACKS = "DELETE_FONT_PACKS";
export function deleteFonts(fontPackIDs: number[], token: string, channel: number): AsyncAction {
	return {
		type: DELETE_FONT_PACKS,
		promise: DeleteFonts(fontPackIDs, token, channel)
	};
}

export const LOAD_COLOR_PACKS = "LOAD_COLOR_PACKS";
export function loadColorPacks(token: string, channel: number): AsyncAction {
	return {
		type: LOAD_COLOR_PACKS,
		promise: GetColors(token, channel)
	};
}

export const SET_SELECTED_COLOR = "SET_SELECTED_COLOR";
export function setSelectedColor(color: Color): Action {
	return {
		type: SET_SELECTED_COLOR,
		payload: color
	};
}

export const ADD_NEW_COLOR_PACK = "ADD_NEW_COLOR_PACK";
export function addNewColorPack(color: Color): Action {
	return {
		type: ADD_NEW_COLOR_PACK,
		payload: color
	};
}

export const SAVE_COLOR_PACK = "SAVE_COLOR_PACK";
export function saveColorPack(color: Color): Action {
	return {
		type: SAVE_COLOR_PACK,
		payload: color
	};
}

export const DELETE_COLOR_PACKS = "DELETE_COLOR_PACKS";
export function deleteColors(colorPackIDs: number[], token: string, channel: number): AsyncAction {
	return {
		type: DELETE_COLOR_PACKS,
		promise: DeleteColors(colorPackIDs, token, channel)
	};
}

export const SET_DESIGN_STYLE = "SET_DESIGN_STYLE";
export function setDesignStyle(style: DesignStyle): Action {
	return {
		type: SET_DESIGN_STYLE,
		payload: style
	};
}

export const SET_DESIGN_SHAPE = "SET_DESIGN_SHAPE";
export function setDesignShape(shape: DesignShape): Action {
	return {
		type: SET_DESIGN_SHAPE,
		payload: shape
	};
}

export const SET_DESIGN_COLORS = "SET_DESIGN_COLORS";
export function setDesignColors(colors: Color): Action {
	return {
		type: SET_DESIGN_COLORS,
		payload: colors
	};
}

export const SET_CUSTOM_CSS = "SET_CUSTOM_CSS";
export function setCustomCSS(css: string): Action {
	return {
		type: SET_CUSTOM_CSS,
		payload: css
	};
}

export const TOGGLE_HOMEPAGE_MODULE = "TOGGLE_HOMEPAGE_MODULE";
export function toggleHomepageModule(module: PageModule): Action {
	return {
		type: TOGGLE_HOMEPAGE_MODULE,
		payload: module
	};
}

export const TOGGLE_POST_REGISTER_HOME_MODULE = "TOGGLE_POST_REGISTER_HOME_MODULE";
export function togglePostRegisterPreviewHomeModule(moduleId: number, is_on: boolean): Action {
	return {
		type: TOGGLE_POST_REGISTER_HOME_MODULE,
		payload: { moduleId, is_on },
	};
}

export const SET_HOMEPAGE_MODULES = "SET_HOMEPAGE_MODULES";
export function setHomepageModules(modules: PageModule[]): Action {
	return {
		type: SET_HOMEPAGE_MODULES,
		payload: modules
	};
}

export const SET_MODULE_ORDER = "SET_MODULE_ORDER";
export function setModuleOrder(newOrder: number[]): Action {
	return {
		type: SET_MODULE_ORDER,
		payload: newOrder
	};
}

export const UPDATE_HOMEPAGE_MODULE = "UPDATE_HOMEPAGE_MODULE";
export function updateHomepageModule(module: PageModule): Action {
	return {
		type: UPDATE_HOMEPAGE_MODULE,
		payload: module
	};
}

export const UPDATE_HOMEPAGE_HEADER = "UPDATE_HOMEPAGE_HEADER";
export function updateHomepageHeader(header: HomepageHeader): Action {
	return {
		type: UPDATE_HOMEPAGE_HEADER,
		payload: header
	};
}

export const UPDATE_HOMEPAGE_MAIN_NAV = "UPDATE_HOMEPAGE_MAIN_NAV";
export function updateHomepageMainNav(navItems: IHomepageMainNavItems[]): Action {
	return {
		type: UPDATE_HOMEPAGE_MAIN_NAV,
		payload: navItems,
	};
}

export const UPDATE_HOMEPAGE_PROFILE_NAV = "UPDATE_HOMEPAGE_PROFILE_NAV";
export function updateHomepageProfileNav(navItems: IHomepageProfileNavItems[]): Action {
	return {
		type: UPDATE_HOMEPAGE_PROFILE_NAV,
		payload: navItems,
	};
}

export const UPDATE_POST_REGISTER_HOME_MODULES = "UPDATE_POST_REGISTER_HOME_MODULES";
export function updatePostRegisterHomeModules(postRegisterHomepageModules: IPostRegisterHomeModule[]): Action {
	return {
		type: UPDATE_POST_REGISTER_HOME_MODULES,
		payload: postRegisterHomepageModules,
	};
}

export const SET_BLOCKED_DOMAIN_EMAILS = "SET_BLOCKED_DOMAIN_EMAILS";
export function setBlockedDomainEmails(domains: string[]): Action {
	return {
		type: SET_BLOCKED_DOMAIN_EMAILS,
		payload: domains
	};
}

export const UPDATE_HOMEPAGE_FOOTER = "UPDATE_HOMEPAGE_FOOTER";
export function updateHomepageFooter(footer: HomepageFooter): Action {
	return {
		type: UPDATE_HOMEPAGE_FOOTER,
		payload: footer
	};
}

export const ADD_PAGE_MODULE = "ADD_PAGE_MODULE";
export function addPageModule(page_module: PageModule): Action {
	return {
		type: ADD_PAGE_MODULE,
		payload: page_module
	};
}

export const ADD_POST_REGISTER_HOME_PAGE_MODULE = "ADD_POST_REGISTER_HOME_PAGE_MODULE";
export function addPostRegisterHomePageModule(token: string, homepage: number, page_module: PageModule): AsyncAction {
	return {
		type: ADD_POST_REGISTER_HOME_PAGE_MODULE,
		promise: AddPostRegisterHomePageModuleToEvent(token, homepage, page_module),
	};
}

export const SET_REGISTRATION_HEADER = "SET_REGISTRATION_HEADER";
export function setRegistrationHeader(text: TranslateString): Action {
	return {
		type: SET_REGISTRATION_HEADER,
		payload: text
	};
}

export const SET_REGISTRATION_DESCRIPTION = "SET_REGISTRATION_DESCRIPTION";
export function setRegistrationDescription(text: TranslateString): Action {
	return {
		type: SET_REGISTRATION_DESCRIPTION,
		payload: text
	};
}

export const SET_REGISTRATION_IMAGE = "SET_REGISTRATION_IMAGE";
export function setRegistrationImage(url: string): Action {
	return {
		type: SET_REGISTRATION_IMAGE,
		payload: url
	};
}

export const SET_REGISTRATION_LOGO = "SET_REGISTRATION_LOGO";
export function setRegistrationLogo(url: string): Action {
	return {
		type: SET_REGISTRATION_LOGO,
		payload: url
	};
}

export const REMOVE_IN_CREATION_PROGRESS_SESSION =
	"REMOVE_IN_CREATION_PROGRESS_SESSION";
export function removeInCreationProgressSession(uid: string | null): Action {
	return {
		type: REMOVE_IN_CREATION_PROGRESS_SESSION,
		payload: uid
	};
}

export const EDIT_SESSION_AGENDA = "EDIT_SESSION_AGENDA";
export function editSessionAgenda(uid: string | null): Action {
	return {
		type: EDIT_SESSION_AGENDA,
		payload: uid
	};
}

export const UPDATE_EDITING_SESSION_BY_UID = "UPDATE_EDITING_SESSION_BY_UID";
export function updateEditingSessionByUid(payload: CreateSession): Action {
	return {
		type: UPDATE_EDITING_SESSION_BY_UID,
		payload
	};
}

export const UPDATE_WORKING_EVENT = "UPDATE_WORKING_EVENT";
export function updateWorkingEvent(event: BrandliveEvent, token: string) {
	return async function(dispatch: Dispatch): Promise<void> {
		try {
			dispatch(isSavingEvent(true));
			await SaveEvent(event, token);
			batch(() => {
				dispatch({
					type: UPDATE_WORKING_EVENT,
					payload: event
				});
				dispatch({
					type: UPDATE_EVENTS_NAME,
					payload: event
				});
				dispatch(isSavingEvent(false));
			});
		} catch (e) {
			console.log(e?.response || e);
		}
	};
}

export const ADD_WORKING_EVENT_HOMEPAGE = "ADD_WORKING_EVENT_HOMEPAGE";
export function addWorkingEventHomepage(uuid: string, token: string, mulitpleSessions?: 'multi'): AsyncAction {
	return {
		type: ADD_WORKING_EVENT_HOMEPAGE,
		promise: AddHomepageToEvent(uuid, token!, mulitpleSessions),
	};
}

export const IS_SAVING = "IS_SAVING";
export function isSavingEvent(isSaving: boolean): Action {
	return {
		type: IS_SAVING,
		payload: isSaving
	};
}

export const SET_AGENDA_IMAGE = "SET_AGENDA_IMAGE";
export function setAgendaImage(url: string | null, session: number): Action {
	return {
		type: SET_AGENDA_IMAGE,
		payload: { url, session },
	};
}

export const PUBLISH_EVENT = "PUBLISH_EVENT";
export function publishEvent(token: string, uuid: string, url: string): AsyncAction {
	return {
		type: PUBLISH_EVENT,
		promise: PublishEvent(token, uuid, url)
	};
}

export const UPDATE_ALL_EVENT_SESSIONS_OVERLAY_CHAT = "UPDATE_ALL_EVENT_SESSIONS_OVERLAY_CHAT";
export function updateAllEventSessionsOverlaChat(isOverlay: boolean): Action {
	return {
		type: UPDATE_ALL_EVENT_SESSIONS_OVERLAY_CHAT,
		payload: isOverlay,
	};
}

export const UPDATE_ALL_EVENT_SESSIONS_LAYOUT_TABS = "UPDATE_ALL_EVENT_SESSIONS_LAYOUT_TABS";
export function updateAllEventSessionsLayoutTabs(layout: SessionTabsLayout): Action {
	return {
		type: UPDATE_ALL_EVENT_SESSIONS_LAYOUT_TABS,
		payload: layout,
	};
}

export const UPDATE_WORKING_EVENT_SESSION = "UPDATE_WORKING_EVENT_SESSION";
export function updateWorkingEventSession(session: Session): Action {
	return {
		type: UPDATE_WORKING_EVENT_SESSION,
		payload: session,
	};
}

export const ADD_REGISTRATION_STEP = "ADD_REGISTRATION_STEP";
export function addRegistrationStep(step: RegistrationStep): Action {
	return {
		type: ADD_REGISTRATION_STEP,
		payload: step
	};
}

export const ADD_TICKETING_SET = "ADD_TICKETING_SET";
export function addTicketingSet(set: TicketingSet): Action {
	return {
		type: ADD_TICKETING_SET,
		payload: set
	};
}

export const REMOVE_TICKETING_SET = "REMOVE_TICKETING_SET";
export function removeTicketingSet(index: number): Action {
	return {
		type: REMOVE_TICKETING_SET,
		payload: index
	};
}

export const UPDATE_TICKETING_SET = "UPDATE_TICKETING_SET";
export function updateTicketingSet(set: TicketingSet, index: number): Action {
	return {
		type: UPDATE_TICKETING_SET,
		payload: { set, index }
	};
}

export const SET_EDITOR_SIZE = "SET_EDITOR_SIZE";
export function setEditorSize(size: 'desktop' | 'tablet' | 'mobile'): Action {
	return {
		type: SET_EDITOR_SIZE,
		payload: size
	};
}

export const TOGGLE_PREVIEW_MODE = "TOGGLE_PREVIEW_MODE";
export function toggleEventPreviewMode(previewMode: string | null): Action {
	return {
		type: TOGGLE_PREVIEW_MODE,
		payload: previewMode
	};
}

export const SET_EVENT_CUSTOM_CSS = "SET_EVENT_CUSTOM_CSS";
export function setEventCustomCss(css: string): Action {
	return {
		type: SET_EVENT_CUSTOM_CSS,
		payload: css
	};
}

export const SET_SOCIAL_SHARING = "SET_SOCIAL_SHARING";
export function setSocialSharing(socialSharing: { image: string, description: string }): Action {
	return {
		type: SET_SOCIAL_SHARING,
		payload: socialSharing
	};
}

export const GET_PUBLISHED_EVENT_URL = "GET_PUBLISHED_EVENT_URL";
export function getPublishedEventUrl(token: string, eventUuid: string): AsyncAction {
	return {
		type: GET_PUBLISHED_EVENT_URL,
		promise: CheckEventPublishedStatus(token, eventUuid)
	};
}

export const UPDATE_PUBLISHED_EVENT_URL = "UPDATE_PUBLISHED_EVENT_URL";
export function updatePublishedUrl(url: string): Action {
	return {
		type: UPDATE_PUBLISHED_EVENT_URL,
		payload: url
	};
}

export const UPDATE_EVENT_FAVICON = "UPDATE_EVENT_FAVICON";
export function updateEventFavicon(token: string, url: string, event_uuid: string): AsyncAction {
	return {
		type: UPDATE_EVENT_FAVICON,
		promise: UpdateEventFavicon(token, url, event_uuid),
	};
}

export const DELETE_EVENT_FAVICON = "DELETE_EVENT_FAVICON";
export function deleteEventFavicon(token: string, event_uuid: string): AsyncAction {
	return {
		type: UPDATE_EVENT_FAVICON,
		promise: DeleteEventFavicon(token, event_uuid),
	};
}
